import { Component, inject, ChangeDetectorRef } from '@angular/core';
import { AskADoctorComponent } from "../ask-a-doctor/ask-a-doctor.component";
import { VirtualUrgentCareComponent } from '../virtual-urgent-care/virtual-urgent-care.component';
import { InPersonUrgentCareComponent } from '../in-person-urgent-care/in-person-urgent-care.component';
import { PrimaryCareProviderComponent } from '../primary-care-provider/primary-care-provider.component';
import { AskADoctorComponent as AskADoctorMobile } from '../../../mobile/components/ask-a-doctor/ask-a-doctor.component';
import { VirtualUrgentCareComponent as VirtualUrgentCareMobile } from '../../../mobile/components/virtual-urgent-care/virtual-urgent-care.component';
import { InPersonUrgentCareComponent as InPersonUrgentCareMobile } from '../../../mobile/components/in-person-urgent-care/in-person-urgent-care.component';
import { PrimaryCareProviderComponent as PrimaryCareProviderMobile } from '../../../mobile/components/primary-care-provider/primary-care-provider.component';
import { NeedHelpDecidingComponent } from '../../../mobile/components/need-help-deciding/need-help-deciding.component';
import { AuthService } from '../../../../shared/services/auth/auth.service';
import { CommonModule } from '@angular/common';
import { GeolocationService } from '../../../../shared/services/geolocation/geolocation.service';
import { MatButtonModule } from '@angular/material/button';
import { MCAppGatewayService } from '../../../../shared/services/mcappgateway/mcappgateway.service';
import { environment } from '../../../../../environments/environment';
import { GetFhirPatientSearchR4Response } from '../../../../shared/model/GetFhirPatientSearchR4Response';
import { LocationCoordinates } from '../../../../shared/model/LocationCoordinates';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { getLocaleFromUrl } from '../../../../shared/services/translate/locale-utils';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { firstValueFrom } from 'rxjs';
import { PostGetPatientInPersonUrgentCareRequest } from '../../../../shared/model/getcarenow/PostGetPatientInPersonUrgentCareRequest';
import { PostGetPatientPrimaryCareProviderRequest } from '../../../../shared/model/getcarenow/PostGetPatientPrimaryCareProviderRequest';
import { PostGetPatientVirtualUrgentCareRequest } from '../../../../shared/model/getcarenow/PostGetPatientVirtualUrgentCareRequest';
import { PostGetQuickCareVisitRequest } from '../../../../shared/model/getcarenow/PostGetQuickCareVisitRequest';
import { MychartWebHeaderComponent } from "../../../../shared/components/mychart-web-header/mychart-web-header.component";
import { IdleTimeoutService } from '../../../../shared/services/idletimeout/idle-timeout.service';
import { MonitoringService } from '../../../../shared/services/monitoring/monitoring-service';

@Component({
  selector: 'app-get-care-now',
  standalone: true,
  imports: [CommonModule, TranslateModule, MatProgressSpinnerModule, MatButtonModule,
    AskADoctorMobile, VirtualUrgentCareMobile, InPersonUrgentCareMobile, PrimaryCareProviderMobile, NeedHelpDecidingComponent, AskADoctorComponent, VirtualUrgentCareComponent, PrimaryCareProviderComponent, InPersonUrgentCareComponent, MychartWebHeaderComponent],
  templateUrl: './get-care-now.component.html',
  styleUrl: './get-care-now.component.css'
})
export class GetCareNowComponent {
  geolocation = inject(GeolocationService);
  mcAppGateway = inject(MCAppGatewayService);
  auth : AuthService = inject(AuthService);
  cdr = inject(ChangeDetectorRef);

  locale: string = '';
  language: string = '';
  currentLocation: LocationCoordinates | null = null;
  isMobileResolution: boolean = false;
  showDivider: boolean = false;

  public title: string = 'Get Care Now';
  public translationsLoaded: boolean = false;
  public eptID: string = '';
  public wprID: string = '';
  public fhirID: string = '';
  public age?: number | null ;
  public gender?: string;
  public patientInfo: GetFhirPatientSearchR4Response | null = null;
  public patientFirstName: string | null = null;
  public proxyName: string | null = null;
  public pcpId: string | null = null;

  quickCareOptions: Array<any> = [];
  inpersonUrgentCareOptions: Array<any> = [];
  virtualUrgentCareOptions: Array<any> = [];
  primaryCareProviderOptions: Array<any> = [];

  constructor(private translate: TranslateService, private idleTimeoutService: IdleTimeoutService, private monitoringService: MonitoringService) {
    this.locale = getLocaleFromUrl();
    sessionStorage.setItem("locale", this.locale);
    this.translate.use(this.locale);
  }

  async ngOnInit() {
    // get first initial from patient name
    this.patientFirstName = sessionStorage.getItem("patientFirstName") ?? ''; 
    this.eptID = sessionStorage.getItem("eptID") || '';

    if (this.locale === 'es-US') {
      this.language = 'spanish'; 
    }
    else {
      this.language = 'english';
    }

    // Dynamically check if window resolution changes
    this.isMobileResolution = window.innerWidth <= 768;
    this.showDivider = window.innerWidth > 1461;
    window.addEventListener('resize', () => {
      this.isMobileResolution = window.innerWidth <= 768;
      this.showDivider = window.innerWidth > 1461;
    });


    this.translate.get('GETCARENOW').subscribe((translated: string) => {
      this.title = translated;
    });

    this.eptID = sessionStorage.getItem("eptID") ?? "";
    this.wprID = sessionStorage.getItem("wprID") ?? "";
    this.fhirID = sessionStorage.getItem("fhirID") ?? "";
    this.age = parseInt(sessionStorage.getItem("age") || '-1', 10);
    if (isNaN(this.age) || this.age < 0) {
      this.age = null;
    }

    this.gender = sessionStorage.getItem("gender") || '';
    this.patientInfo = JSON.parse(sessionStorage.getItem("patientInfo") || '{}') as GetFhirPatientSearchR4Response;  

    const locationPermissionAllowed = localStorage.getItem('locationPermissionAllowed');
    const storedLocation = localStorage.getItem('currentLocation');

    // If we don't know the user's location permission status, attempt to get location 
    // (which will trigger permission prompt if not already granted/denied)
    if (locationPermissionAllowed === 'false') {
      // User has previously denied location permission, 
      // so we can skip trying to get location and just fetch data without it
      this.getData();
    }
    else if (locationPermissionAllowed === 'true' && storedLocation) {
      try {
        this.currentLocation = JSON.parse(storedLocation) as LocationCoordinates;
      } catch (err) {
        console.error("Failed to parse stored location:", err);
        localStorage.removeItem('currentLocation'); // Clear invalid data
      }
      this.getData(); // Fetch care options immediately, using stored location if available
      
    }
    else {
      const geolocationPromise = this.geolocation.getCurrentLocationAsync();
      const timeoutPromise = new Promise<LocationCoordinates>((_, reject) =>
        setTimeout(() => reject(new Error('Geolocation timeout')), 2000) // 2 seconds timeout
      );

      await Promise.race([geolocationPromise, timeoutPromise])
        .then(location => {
          this.currentLocation = location;  
        })
        .catch(err => {
          console.error("Geolocation failed:", err);
        })
        .finally(() => {
          this.getData();
        });
    }

    // Start watching for user inactivity
    if(this.eptID && this.eptID != '') {
      this.idleTimeoutService.startWatching(false, null); // not mobile app, not plus app
    }

    this.monitoringService.logEvent('Login', { feature: 'get-care-now', platform: 'web' });
  }

  async allowLocation() {
    this.geolocation.getCurrentLocation().subscribe(location => {
      this.currentLocation = location;
      this.getData();
    });
  }

  async loadTranslations() {
    try {
      // Switch to the selected locale and wait for the translation file to fully load
      const translations = await firstValueFrom(this.translate.use(this.locale));
      
      // Verify translations are actually loaded by checking if we get a real value
      const titleKey = 'GETCARENOW';
      this.title = this.translate.instant(titleKey);
      
      // If instant returns the key itself, translations aren't loaded yet
      if (this.title === titleKey) {
        console.warn('Translation not immediately available, waiting...');
        // Fall back to async method
        this.title = await firstValueFrom(this.translate.get(titleKey));
      }
      
      // Mark translations as loaded so child components can render
      this.translationsLoaded = true;
      this.cdr.detectChanges();
      
      console.log('Translations fully loaded and ready');
    } catch (error) {
      console.error('Error loading translations:', error);
      // Set fallback values if translation loading fails
      this.title = 'Get Care Now';
      this.translationsLoaded = true; // Allow rendering even on error
    }
  }

  getData() {
    this.getQuickCareOptions();
    this.getVirtualUrgentCareOptions();
    this.getInPersonUrgentCareOptions();
    this.getPrimaryCareProviderOptions();
  }
  
  getQuickCareOptions() {
    let quickCareRequest = new PostGetQuickCareVisitRequest();
    quickCareRequest.age = this.age || null;
    quickCareRequest.gender = this.gender || null;
    quickCareRequest.language = this.locale || 'en-US';  
    quickCareRequest.maxDisplayCount = this.isMobileResolution ? 3 : 2;

    this.mcAppGateway.PostGetQuickCareVisit(quickCareRequest).subscribe({
      next: (response) => {
        console.log("Quick Care options fetched successfully:", response);
        this.quickCareOptions = response || [];
      }
    });
  }
  
  getVirtualUrgentCareOptions() {
    let vucRequest = new PostGetPatientVirtualUrgentCareRequest();
    vucRequest.patientId = this.eptID;
    vucRequest.fhirPatientID = this.fhirID;
    vucRequest.patientAge = this.age ?? 0;
    vucRequest.maxDisplayCount = this.isMobileResolution ? 3 : 2;

    this.mcAppGateway.PostGetPatientVirtualUrgentCare(vucRequest).subscribe({
      next: (response) => {
        console.log("Virtual Urgent Care options fetched successfully:", response);
        this.virtualUrgentCareOptions = response || [];
      }
    });
  }

  getInPersonUrgentCareOptions() {
    let ipucRequest = new PostGetPatientInPersonUrgentCareRequest();
    ipucRequest.patientId = this.eptID;
    ipucRequest.fhirPatientID = this.fhirID;
    ipucRequest.maxDisplayCount = this.isMobileResolution ? 3 : 2;

    if (this.currentLocation != null) {
      ipucRequest.latitude = +(this.currentLocation.latitude);
      ipucRequest.longitude = +(this.currentLocation.longitude);
    }
    this.mcAppGateway.PostGetPatientInPersonUrgentCare(ipucRequest).subscribe({
      next: (response) => {
        console.log("In-Person Urgent Care options fetched successfully:", response);
        this.inpersonUrgentCareOptions = response || [];
        this.inpersonUrgentCareOptions.forEach(element => {
          element.fullAddress = element.providerName + element.address1 + (element.city ? ', ' + element.city : '') + (element.zip ? ' ' + element.zip : '');
        });
      }
    });
  }
  
  getPrimaryCareProviderOptions() {
    let request = new PostGetPatientPrimaryCareProviderRequest();
    request.patientId = this.eptID;
    request.myChartAccountId = this.wprID ?? "";
    request.patientAge = this.age ?? request.patientAge;
    request.maxDisplayCount = this.isMobileResolution ? 3 : 2;

    if (this.currentLocation != null) {
      request.latitude = +(this.currentLocation.latitude);
      request.longitude = +(this.currentLocation.longitude);
    }
    this.mcAppGateway.PostGetPatientPrimaryCareProvider(request).subscribe({
      next: (response) => {
        console.log("Primary Care Provider options fetched successfully:", response);
        this.primaryCareProviderOptions = response || [];
        this.pcpId = this.primaryCareProviderOptions.find((prov) => prov.specialties == "Primary Care Provider")?.providerId ?? null;
      }
    });
  }

}
